import { SortingCriterionOrderEnum } from './generic.types';
import { type IAdmingSettingsAssignedPatientCohort } from './patients.types';

export type IResolvedPatientRoute = {
  patientId: string;
  cohortId: string;
  detailViewId: string;
};

export enum ListColumnsEnum {
  ASSIGNMENTS = 'assignments',
  PATIENT = 'patient name',
  AGE = 'age',
  ENROLLED = 'enrolled',
  ENGAGEMENT = 'engagement',
  ADHERENCE_7_DAYS = 'adherence (7d)',
  BLOOD_GLUCOSE = 'blood glucose',
  BMI = 'BMI',
  GLUCOSE = 'glucose',
  GLUCOSE_7_DAYS = 'glucose trailing (7d)',
  CGM_GLUCOSE = 'cgm glucose',
  STICKIES = 'stickies',
  RESTING_HR = 'resting HR',
  BLOOD_PRESSURE = 'blood pressure',
  BG_BT_DAYS = 'bg bt days',
  BP_BT_DAYS = 'bp bt days',
  WEIGHT_BT_DAYS = 'weight bt days',
  RH_BT_DAYS = 'rhr bt days',
  WT_CHANGE_PERCENT_1_DAY = 'wt chg % (1d)',
  WT_CHANGE_PERCENT_2_DAY = 'wt chg % (2d)',
  WT_CHANGE_PERCENT_3_DAY = 'wt chg % (3d)',
  WT_CHANGE_PERCENT_1_WEEK = 'wt chg % (1w)',
  WT_CHANGE_PERCENT_2_WEEK = 'wt chg % (2w)',
  WT_CHANGE_PERCENT_1_MONTH = 'wt chg % (1mo)',
  WT_CHANGE_POUNDS_1_DAY = 'wt chg lbs (1d)',
  WT_CHANGE_POUNDS_2_DAY = 'wt chg lbs (2d)',
  WT_CHANGE_POUNDS_3_DAY = 'wt chg lbs (3d)',
  WT_CHANGE_POUNDS_1_WEEK = 'wt chg lbs (1w)',
  WT_CHANGE_POUNDS_2_WEEK = 'wt chg lbs (2w)',
  WT_CHANGE_POUNDS_1_MONTH = 'wt chg lbs (1mo)',
  GLUCOSE_TIME_IN_RANGE_7_DAYS = 'glucose time in range (7d)',
  GLUCOSE_TIME_IN_ZONE = 'glucose (2w)',
  GLUCOSE_TIME_IN_ZONE_4_WEEK = 'glucose (4w)',
  BP_TIME_IN_ZONE = 'bp (4w)',
  PHONE_NUMBER = 'phone number',
  LATEST_GLUCOSE_MEASUREMENT = 'latest glucose measurement',
  LATEST_INSULIN_MEASUREMENT = 'latest insulin measurement',
  LATEST_WEIGHT_READING = 'latest weight measurement',
  LATEST_BLOOD_PRESSURE_MEASUREMENT = 'latest bp measurement',
  LATEST_RESTING_HEART_RATE_MEASUREMENT = 'latest rhr measurement',
  LATEST_IRREGULAR_HEART_RHYTHM_MEASUREMENT = 'latest ihr measurement',
  MESSAGES = 'messages',
  RECEIVED = 'received',
  GLUCOSE_TIME_SERIES_TODAY = 'bg today',
  GLUCOSE_TIME_SERIES_YESTERDAY = 'bg yesterday',
  BP_TIME_SERIES_TODAY = 'bp today',
  BP_TIME_SERIES_YESTERDAY = 'bp yesterday',
  DOB = 'DOB',
  WEIGHT = 'weight',
  CALORIES_2_WEEK = 'calories 2w',
  TRIAGE_REASON = 'reason',
  TRIAGE_ALERT = 'priority',
  TRIAGE_ALERT_TRIGGERED = 'triggered',
  TEMPERATURE = 'temperature',
}

export enum HistogramTypesEnum {
  ADHERENCE = 'adherence',
  BLOOD_GLUCOSE_TREND_FIRST_30_DAYS = 'blood_glucose_trend_first_30_days',
  BLOOD_GLUCOSE_TREND_FIRST_60_DAYS = 'blood_glucose_trend_first_60_days',
  BLOOD_GLUCOSE_TREND_FIRST_90_DAYS = 'blood_glucose_trend_first_90_days',
}

export enum ListWidgetsEnum {
  ADHERENCE_HISTOGRAM = 'Adherence Histogram',
  BLOOD_GLUCOSE_TREND_FIRST_30_DAYS = 'Blood Glucose Trend First 30 Days',
  BLOOD_GLUCOSE_TREND_FIRST_60_DAYS = 'Blood Glucose Trend First 60 Days',
  BLOOD_GLUCOSE_TREND_FIRST_90_DAYS = 'Blood Glucose Trend First 90 Days',
}

export interface IListAvailableColumn {
  index: number;
  value: string;
  hidden: boolean;
}

export enum ListLayoutsEnum {
  ROWS = 'List Only',
  WIDGETS_2_ROWS = '2_WIDGETS_ROWS',
  WIDGETS_3_ROWS = '3_WIDGETS_ROWS',
}

export enum ListLayoutWidgetsEnum {
  ADHERENCE_HISTOGRAM = 'Adherence Histogram',
  BLOOD_GLUCOSE_TREND_FIRST_30_DAYS = 'Blood Glucose Trend First 30 Days',
  BLOOD_GLUCOSE_TREND_FIRST_60_DAYS = 'Blood Glucose Trend First 60 Days',
  BLOOD_GLUCOSE_TREND_FIRST_90_DAYS = 'Blood Glucose Trend First 90 Days',
  NO_WIDGET = 'No Widget',
}

export enum DetailPatientDataEnum {
  PHONE = 'phone',
  SECOND_PHONE = '2nd phone',
  MRN = 'MRN',
}

export interface IDetailAvailablePatientData {
  index: number;
  value: keyof typeof DetailPatientDataEnum;
  displayValue: string;
  hidden: boolean;
}

export enum DetailTabChartsEnum {
  BLOOD_GLUCOSE = 'blood glucose',
  BLOOD_PRESSURE = 'blood pressure',
  HEART_RESTING = 'heart resting',
  IRREGULAR_PULSE = 'irregular pulse',
  INSULIN = 'insulin',
  SPACER = 'spacer',
  ACTIVITY = 'activity',
  WEIGHT = 'weight',
  CALORIES = 'calories',
  ENGAGEMENT = 'engagement',
  ADHERENCE = 'adherence',
  SYMPTOMS = 'symptoms',
  MEDICATIONS = 'medications',
  TEMPERATURE = 'temperature',
  CHECK_IN = 'check in',
}

export enum DetailTabRowsEnum {
  CAREPLAN = 'Care Plan',
  SIGMA = 'Sigma Care Flows',
  SPACER = 'spacer',
  TIMELINE = 'Timeline',
}

export enum DetailViewColumnsEnum {
  CAREPLAN = 'Care Plan',
  SPACER = 'spacer',
  STICKIES = 'Stickies',
  TIMELINE = 'Timeline',
}

export enum DetailTabColumns3Enum {
  ADHERENCE = 'Adherence',
  APPOINTMENT = 'Appointment',
  BLOOD_GLUCOSE = 'Blood Glucose',
  BLOOD_PRESSURE = 'Blood Pressure',
  COMMUNICATION = 'Communication',
  DEVICES = 'Devices',
  GOALS = 'Goals',
  SMART_GOALS = 'SMART Goals',
  LIFESTYLE = 'Lifestyle',
  MEDICATIONS = 'Medications',
  STICKIES = 'Stickies',
  SURVEYS = 'Surveys',
  SPACER = 'spacer',
  VITALS = 'Vitals',
  SOAP_NOTES = 'Soap Notes',
  TASKS = 'Tasks',
  TIME_TRACKER = 'Time Tracker',
  MY_WHY = 'My Why',
  DOCUMENTS = 'Documents',
  ZENDESK_TICKETS = 'Zendesk Tickets',
  CHECK_IN = 'Weekly Check-in',
}

export interface IDetailAvailableTabWidget {
  index: number;
  value: DetailTabRowsEnum | DetailTabChartsEnum | string;
  hidden: boolean;
}

export enum DetailTemplatesEnum {
  ROWS = 'Rows',
  TIME_SERIES_CHARTS = 'Time Series Charts',
  COLUMNS = '2:1 Columns',
  COLUMNS_3 = '3 Columns',
  ROWS_AND_COLUMNS_3 = 'Full Width + 3 Columns',
}

export enum DetailLayoutsEnum {
  DIABETES_STANDARD = 'diabetes_standard',
  HEART_FAILURE_STANDARD = 'heart_failure_standard',
}

export enum ViewTypeEnum {
  LIST = 'LIST',
  DETAIL = 'DETAIL',
}

export enum PatientDetailViewEnum {
  OVERVIEW = 'OVERVIEW',
  CHARTS = 'CHARTS',
  CARE_PLAN = 'CARE_PLAN',
}

export enum ViewTagTypeEnum {
  NONE = 'None',
  GENERAL = 'General',
  HYPERTENSION = 'Hypertension',
  DIABETES = 'Diabetes',
  WEIGHT_MANAGEMENT = 'Weight Management',
}

export interface IDetailView {
  dateCreated?: string;
  dateUpdated?: string;
  dateDeleted?: string;
  id?: string;
  layout: keyof typeof DetailLayoutsEnum | string;
  name: string;
  type: ViewTypeEnum;
  headers: string[];
  tabs: IDetailTabObject[];
}

export interface IDetailTabObject {
  name: string;
  layout: DetailTemplatesEnum;
  rows: string[] | null;
  widgets: string[] | null;
}

export interface IComposableViews {
  detailViews: IDetailView[];
}

export interface ISelectorView {
  columns: string[];
  dateCreated?: string;
  dateUpdated?: string;
  dateDeleted?: string;
  filters: string[];
  id?: string;
  indicators: IIndicators;
  showIndicator?: boolean;
  color?: string;
  name: string;
  sortingCriterion: ISortingCriterion;
  subselectors: ISubSelector[];
  newSectionName?: string;
  patientsCount?: number;
}

export interface IIndicators {
  unreviewed: boolean;
  triageLabel: string;
}

export interface ICohortView {
  cohortId: string;
  dateCreated?: string;
  dateUpdated?: string;
  dateDeleted?: string;
  detailViewId: string;
  detailView?: string;
  id?: string;
  name: string;
  selectors: ISelectorView[];
  selectorCountsFetched?: boolean;
  isChecked?: boolean;
  cohortName?: string;
  type?: string;
}

export interface ICohortDetails {
  cohort: {
    name: string;
    description: string;
    remoteId: string;
  };
  cohortView: {
    name: string;
    detailView: string;
    detailViewId: string;
    cohortId: string;
    id: string;
  };
}

export interface ICohortViewType {
  cohortViews: ICohortView[];
}

export interface ISelectorViewType {
  selectors: ISelectorView[];
}

export interface ISortingCriterion {
  applied: boolean;
  displayName: string;
  order: SortingCriterionOrderEnum | string;
  value: string;
}

export interface ISubSelector {
  columns: string[];
  filters: string[];
  id?: string;
  name: string;
  indicators: IIndicators;
  showIndicator?: boolean;
  color?: string;
  sortingCriterion: ISortingCriterion;
  patientsCount?: number;
}

export interface ISectionColumns {
  index: number;
  columns: string[];
}

export interface IUserPatientDetailProps {
  userDetailViewTabs: IDetailTabObject[];
  userDetailViewHeaders: string[];
}

export interface IAutoassignCohortResult {
  active: ICohortAutoassign;
  referral: ICohortAutoassign;
}

export interface IAutoassignCohortSet {
  cohort: {
    remoteId: string;
  };
}

export interface ICohortAutoassign {
  remoteId: string;
  name: string;
}

export interface ICohort {
  dateCreated?: string;
  dateDeleted?: string;
  dateUpdated?: string;
  description?: string;
  name: string;
  remoteId: string;
  patients?: number;
  touched?: boolean;
  checked?: boolean;
}

export interface IAssignedCohortsGenericType {
  cohorts: IAdmingSettingsAssignedPatientCohort[];
}

export interface ICohortsGenericType {
  cohorts: ICohort[];
}

export interface ICohortGenericType {
  cohort: ICohort;
}

export interface ICohortsToAssignToPatients {
  cohorts: ICohort[];
  patientId: string;
}

export interface IEHRSettingsAssignment {
  assignment: IEHRSettingsAssignmentData;
}

export interface IEHRSettingsAssignmentData {
  addresses: string;
  enabled: boolean;
  title: string;
}

export interface IEHRSettingsSystemDataToSend {
  clientId: string;
  url: string;
  id: string;
}

export interface IEHRSettingsSystemsToSend {
  system: IEHRSettingsSystemData | IEHRSettingsSystemDataToSend;
}

export interface IEHRSettingsSystemData {
  [key: string]: string | boolean;
}

export interface IEHRSettingsSystemDataWithVendors {
  settings: IEHRSettingsSystemData;
  vendors: string[];
}

export interface IEHRSettingVerOne {
  clientId?: string;
  enabled?: boolean | string;
  id?: string;
  name?: string;
  secret?: string;
  url?: string;
  vendor?: string;
}

export interface IEHRSettingsSystemDataVerOne {
  settings: IEHRSettingVerOne[];
  vendors: string[];
}

export interface IEHRSettingsSystems {
  system: IEHRSettingsSystemData;
}

export interface IEHRSystemsControlInfo {
  control: string;
  value: string;
  isSystem: boolean;
}

export interface IEHRSystemsPatientConfig {
  patientConfig: IEHRSettingsSystemData;
  patientConfigIndex: number;
  isDelete: boolean;
}

export interface IEHRSettingsSmart {
  iss: string;
  orgID: string;
}

export interface IEHRSettings {
  assignment: IEHRSettingsAssignment;
  reads: IEHRSettingsMeasurementsReads;
  writes: IEHRSettingsMeasurementsWrites;
}

export interface IEHRSettingsMeasurements {
  reads: IEHRSettingsMeasurementsReads;
  writes: IEHRSettingsMeasurementsWrites;
}

export interface IEHRSettingsMeasurementsReads {
  [key: string]: IEHRSettingsMeasurementsRead;
}

export interface IEHRSettingsMeasurementsRead {
  enabled: boolean;
}

export interface IEHRSettingsMeasurementsWrites {
  [key: string]: IEHRSettingsMeasurementsWrite;
}

export interface IEHRSettingsMeasurementsWrite {
  allowManual: boolean;
  code: string;
  enabled: boolean;
  system: string;
}

export interface ISettingsProvidersData {
  providers: string[];
}

export interface ISettingsFaxData {
  recipientNumber: string;
  dateUpdated?: string;
}

export interface ISettingsAccessData {
  mfa: ISettingsMFA;
  restrictedDomains: string[];
  restrictedEmails: string[];
}

export interface ISettingsMFA {
  daysToDisable?: number;
  enabled: boolean;
}

export interface ISettingsSliderAlertSettingsData {
  [key: string]:
    | ISettingsAlertsMeasurement
    | ISettingsAlertsBloodPressureMeasurement
    | ISettingsAlertsWeightChangeMeasurement;
}

export interface ISettingsTimeInZoneAlertSettingsData {
  [key: string]:
    | ISettingsGlucoseTimeInZoneAlertsData
    | ISettingsBloodPressureTimeInZoneAlertsData;
}

export interface ISettingsAlertsData {
  [key: string]:
    | ISettingsAlertsMeasurement
    | ISettingsAlertsBloodPressureMeasurement
    | ISettingsGlucoseTimeInZoneAlertsData
    | ISettingsBloodPressureTimeInZoneAlertsData
    | ISettingsAlertsWeightChangeMeasurement;
}

export interface ISettingsGlucoseTimeInZoneAlertsData {
  veryLow: ISettingsTimeInZoneAlertsThreshold;
  low: ISettingsTimeInZoneAlertsThreshold;
  normal: ISettingsTimeInZoneAlertsThreshold;
  high: ISettingsTimeInZoneAlertsThreshold;
  veryHigh: ISettingsTimeInZoneAlertsThreshold;
}

export interface ISettingsBloodPressureTimeInZoneAlertsData {
  hypotension: ISettingsTimeInZoneAlertsThreshold;
  normal: ISettingsTimeInZoneAlertsThreshold;
  elevated: ISettingsTimeInZoneAlertsThreshold;
  stage1: ISettingsTimeInZoneAlertsThreshold;
  stage2: ISettingsTimeInZoneAlertsThreshold;
  crisis: ISettingsTimeInZoneAlertsThreshold;
}

export interface ISettingsTimeInZoneAlertsThreshold {
  threshold: number;
}

export interface ISettingsAlertsBloodPressureMeasurement {
  value: ISettingsBloodPressureAlertsMeasurementValues;
}

export interface ISettingsAlertsMeasurement {
  change: ISettingsAlertsMeasurementValues;
  value: ISettingsAlertsMeasurementValues;
}

export interface ISettingsAlertsMeasurementValues {
  criticalHigh: number;
  criticalLow: number;
  high: number;
  low: number;
}

export interface ISettingsBloodPressureAlertsMeasurementValues {
  crisis: number;
  stage2: number;
  stage1: number;
  elevated?: number;
  hypotension: number;
}

export interface ISettingsAlertsMeasurementData {
  isHigh: boolean;
  color: string;
  ruleType: string;
  measurementName: string;
  measurementValues: ISettingsAlertsMeasurement;
}

export interface ISettingsAlertsWeightChangeMeasurement {
  change: ISettingsWeightChangeAlertsMeasurementValues;
}

export interface ISettingsWeightChangeAlertsMeasurementValues {
  criticalHigh: number;
  criticalLow: number;
}

export interface ISettingsTags {
  categories: ISettingsTagsCategory[];
}

export interface ISettingsTagsWithCategories {
  categories: ISettingsTagsCategory[];
  tags: string[];
}

export interface ISettingsTagsCategory {
  name: string;
  color: string | null;
}

export interface ITagCategoryWithTags {
  [key: string]: string[];
}

export interface ITagsWithUserId {
  userId: string;
  tags: string[];
}

export interface IPatientTagsResponse {
  patients: ITagsWithUserId[];
}

export interface ISettingsBrandingFileInfo {
  file: File | null;
  fileName: string;
  fileSize: string;
}

export interface ISettingsBrandingDomain {
  subdomain: string;
}

export interface ISunsetBanner {
  date?: string;
  message?: string;
}

export interface IDetailViewChartsWindowOption {
  time: string;
}

export interface ISelectorsCount {
  selectors: ISelectorPatientCount[];
  totalPatients: number;
  currentPatients: number;
}

export interface ISelectorCountsFetched {
  [key: string]: {
    selectors: ISelectorView[];
    timestamp: number;
  };
}

export interface ISelectorPatientCount {
  name: string;
  count: number;
  indicators: IIndicators;
}

export interface ISelectorsPatientCountPayload {
  selectors: ISelectorPatientCountPayload[];
}

export interface ISelectorPatientCountPayload {
  name: string;
  filtersString: string;
}

export interface IPatientUpdatedCohorts {
  add: string[];
  remove: string[];
}
